import React, { useState, useEffect, useMemo } from 'react';
import { BreakpointProps, ResponsiveComponentProps } from './ResponsiveComponent.types';

export const Breakpoint = <T,>({ children }: BreakpointProps<T>) => {
  return <>{children}</>;
};

const ResponsiveComponent = <T,>(props: ResponsiveComponentProps<T>) => {
  const { component: Component, children } = props;

  // Parse each child of ResponsiveComponent
  const parsedBreakpoints = (React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Breakpoint && child.props.children) {
      // Get the width of the breakpoint
      const width = child.props.width as number;
      // Get the children props of the breakpoint that match the component type
      const childProps = React.Children.map(child.props.children, (breakpointChild) => {
        // Check if the child is a valid React element and if it is the same type as the component
        if (React.isValidElement(breakpointChild) && breakpointChild.type === Component) {
          const breakpointChildProps = breakpointChild.props;
          return breakpointChildProps;
        }
      });
      return {
        width,
        childProps: childProps[0],
      };
    }
  }) || [])
  .filter(Boolean)
  // Reduce the breakpoints to an object with the width as the key and the child props as the value
  .reduce<Record<number, any>>((acc, curr) => {
    acc[curr.width] = curr.childProps;
    return acc;
  }, {}) || {};

  const [activeBreakpoint, setActiveBreakpoint] = useState<number | null>(null);
  const breakpoints = useMemo(() => {
    // Derive breakpoints from the propsMap keys
    return Object.keys(parsedBreakpoints)
      .map((key) => parseInt(key, 10))
      .sort((a, b) => b - a); // Sort descending for easier matching
  }, [parsedBreakpoints]);

  useEffect(() => {
    const breakpoints = Object.keys(parsedBreakpoints)
      .map((key) => parseInt(key, 10))
      .sort((a, b) => b - a); // Sort descending

    // Create an array of media query objects based on the breakpoints
    const mediaQueries = breakpoints.map((breakpoint) => ({
      // The current breakpoint value (e.g., 375, 768, 1024, etc.)
      breakpoint,

      // Create a media query for the current breakpoint using matchMedia
      // The query checks if the viewport's width is at least the specified breakpoint value
      query: window.matchMedia(`(min-width: ${breakpoint}px)`),
    }));


    const updateActiveBreakpoint = () => {
      // Find the first media query that matches the current viewport width
      const matchingQuery = mediaQueries.find(({ query }) => query.matches);

      // If a matching query is found, set the active breakpoint to this query's breakpoint value
      if (matchingQuery) {
        setActiveBreakpoint(matchingQuery.breakpoint);
      } else {
        // If no query matches (i.e., the viewport width is smaller than the smallest breakpoint),
        // fallback to the smallest breakpoint in the breakpoints array
        setActiveBreakpoint(Math.min(...breakpoints));
      }
    };

    // Attach listeners
    mediaQueries.forEach(({ query }) =>
      query.addEventListener('change', updateActiveBreakpoint)
    );

    // Set the initial breakpoint
    updateActiveBreakpoint();

    return () => {
      // Cleanup listeners
      mediaQueries.forEach(({ query }) =>
        query.removeEventListener('change', updateActiveBreakpoint)
      );
    };
  }, [parsedBreakpoints]);

   // Fallback to the smallest breakpoint if none match
   const fallbackBreakpoint = Math.min(...breakpoints);
 
   // Use the active breakpoint or fallback to the smallest
   const breakpoint = activeBreakpoint ?? fallbackBreakpoint;

  // Get the props for the breakpoint
  const activeProps = parsedBreakpoints[breakpoint];

  return <Component {...activeProps} />;
};

export default ResponsiveComponent;
